/**
 * PageHeading — reusable two-line hero heading used at the top of list/browse pages.
 *
 * Renders a small spaced subtitle above a large bold title split across two lines,
 * with an optional description text anchored to the bottom-right.
 *
 * Usage:
 * ```kotlin
 * PageHeading(
 *     subtitle = "FREE · 4K · 8K RESOLUTION",
 *     titleLine1 = "TOP",
 *     titleLine2 = "Wallpapers",
 *     description = "Curated collection of high-resolution wallpapers for every screen. Free to download, forever.",
 * )
 * ```
 */
package org.moxultimate.topwithmox.components.texts

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.moxultimate.topwithmox.theme.FontFamily

/**
 * Hero page heading with a small label above, a large bold two-line title,
 * and an optional description anchored to the bottom-right.
 *
 * @param subtitle    Small uppercase label above the title (e.g. "FREE · 4K · 8K RESOLUTION").
 * @param titleLine1  First line of the large title (e.g. "TOP").
 * @param titleLine2  Second line of the large title (e.g. "Wallpapers").
 * @param description Optional gray body copy shown bottom-right of the title block.
 * @param modifier    Additional layout modifiers applied to the outer Row.
 */
@Composable
fun PageHeading(
    subtitle: String,
    titleLine1: String,
    titleLine2: String,
    description: String? = null,
    detail: String,
    detailType1: String,
    detail2: String,
    detailType2: String,
    detail3: String,
    detailType3: String,
    modifier: Modifier = Modifier,
) {
    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint <= Breakpoint.SM

    // Reusable text base to drastically cut down boilerplate
    val baseTextModifier = Modifier.fontFamily(*FontFamily.toTypedArray())

    if (isMobile) {
        PageHeadingMobile(
            subtitle = subtitle,
            titleLine1 = titleLine1,
            titleLine2 = titleLine2,
            description = description,
            detailType1 = detailType1,
            detailType2 = detailType2,
            detailType3 = detailType3,
            detail = detail,
            detail2 = detail2,
            detail3 = detail3,
            baseTextModifier = baseTextModifier,
        )

    } else {
        PageHeadingDesktop(
            subtitle = subtitle,
            titleLine1 = titleLine1,
            titleLine2 = titleLine2,
            description = description,
            detailType1 = detailType1,
            detailType2 = detailType2,
            detailType3 = detailType3,
            detail = detail,
            detail2 = detail2,
            detail3 = detail3,
            baseTextModifier = baseTextModifier,
            modifier = modifier,
        )
    }

}

@Composable
private fun PageHeadingDesktop(
    subtitle: String,
    titleLine1: String,
    titleLine2: String,
    description: String? = null,
    detail: String,
    detailType1: String,
    detail2: String,
    detailType2: String,
    detail3: String,
    detailType3: String,
    baseTextModifier: Modifier,
    modifier: Modifier = Modifier,
) {
    // Desktop: side-by-side row with title left and description right, vertically centered
    Row(
        modifier = modifier
            .fillMaxWidth()
            .height(360.px)
            .padding(top = 140.px, bottom = 64.px),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Left Column: Subtitle and Main Titles
        Column(modifier = Modifier.weight(1f)) {
            SpanText(
                text = subtitle,
                modifier = baseTextModifier
                    .fontSize(12.px)
                    .fontWeight(FontWeight.Normal)
                    .letterSpacing(0.12.em)
                    .textTransform(TextTransform.Uppercase)
                    .color(Colors.Gray)
                    .padding(bottom = 6.px),
            )
            SpanText(
                text = titleLine1,
                modifier = baseTextModifier
                    .fontSize(75.px)
                    .fontWeight(FontWeight.Bold)
                    .lineHeight(1.em)
                    .moxTextGradient(),
            )
            SpanText(
                text = titleLine2,
                modifier = baseTextModifier
                    .fontSize(65.px)
                    .fontWeight(FontWeight.Bold)
                    .lineHeight(1.em)
                    .moxTextGradient(),
            )
        }

        // Right Column: Optional Description and Stats Block
        if (description != null) {
            Column(modifier = Modifier.width(400.px)) {
                SpanText(
                    text = description,
                    modifier = baseTextModifier
                        .fontSize(16.px)
                        .fontWeight(FontWeight.Normal)
                        .lineHeight(1.6.em)
                        .color(Colors.Gray),
                )
                if (detail.isNotEmpty()) {
                    Row(
                        modifier = Modifier.padding(top = 24.px),
                        horizontalArrangement = Arrangement.spacedBy(40.px)
                    ) {
                        DetailsText(detail, detailType1, Colors.Gray, FontWeight.SemiBold)
                        DetailsText(detail2, detailType2, Colors.Gray, FontWeight.SemiBold)
                        DetailsText(detail3, detailType3, Colors.Gray, FontWeight.SemiBold)
                    }
                }
            }
        }
    }
}

@Composable
private fun PageHeadingMobile(
    subtitle: String,
    titleLine1: String,
    titleLine2: String,
    description: String? = null,
    detail: String,
    detailType1: String,
    detail2: String,
    detailType2: String,
    detail3: String,
    detailType3: String,
    baseTextModifier: Modifier,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier =
            modifier
                .fillMaxWidth()
                .padding(top = 120.px, bottom = 30.px),
    ) {
        SpanText(
            text = subtitle,
            modifier = baseTextModifier
                .fontSize(11.px)
                .fontWeight(FontWeight.Normal)
                .letterSpacing(0.12.em)
                .textTransform(TextTransform.Uppercase)
                .color(Colors.Gray)
                .padding(bottom = 6.px),
        )
        SpanText(
            text = titleLine1,
            modifier = baseTextModifier
                .fontSize(52.px)
                .fontWeight(FontWeight.Bold)
                .lineHeight(1.em)
                .moxTextGradient(),
        )
        SpanText(
            text = titleLine2,
            modifier = baseTextModifier
                .fontSize(40.px)
                .fontWeight(FontWeight.Bold)
                .lineHeight(1.em)
                .moxTextGradient(),
        )
        if (detail.isNotEmpty()) {
            Row(
                modifier = Modifier.padding(top = 16.px),
                horizontalArrangement = Arrangement.spacedBy(24.px)
            ) {
                DetailsText(detail, detailType1, Colors.Gray, FontWeight.SemiBold)
                DetailsText(detail2, detailType2, Colors.Gray, FontWeight.SemiBold)
                DetailsText(detail3, detailType3, Colors.Gray, FontWeight.SemiBold)
            }
        }
    }
}
